import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { isCashBasedNonExactSuggestion } from '@features/reconciliation/utils'

import { usePolling } from '../../../hooks/usePolling'
import { PayableSubject } from '../../paymentReconciliationModal/types'
import { getSubjectReconciliationMatches } from '../query-api'
import { getReconciliationQueryKeyFromSubject, getTypeFromPayableSubject } from '../utils'

const MAX_POLLING_ATTEMPTS = 4

export const useSubjectReconciliationSuggestions = (subject?: PayableSubject) => {
  const enableSuggestionQuerying = useMemo(() => {
    return !!subject?.organizationId && !subject.isPaid
  }, [subject])

  const { data, isLoading, refetch } = useQuery(
    [getReconciliationQueryKeyFromSubject(subject), subject?.id],
    () =>
      getSubjectReconciliationMatches({
        organizationId: subject?.organizationId as string,
        subjectId: subject?.id as string,
        subjectType: getTypeFromPayableSubject(subject),
      }),
    {
      enabled: enableSuggestionQuerying,
      refetchOnMount: false,
      keepPreviousData: true,
      notifyOnChangeProps: ['data', 'isLoading'],
    },
  )

  const isSubjectApprovedToday = useMemo(() => {
    if (!subject?.approvedTime) {
      return false
    }
    const today = new Date().setHours(0, 0, 0, 0)

    return new Date(subject.approvedTime).setHours(0, 0, 0, 0) === today
  }, [subject?.approvedTime])
  usePolling({
    onPoll: refetch,
    isEnabled: data?.suggestions.length === 0 && isSubjectApprovedToday,
    maxAttempts: MAX_POLLING_ATTEMPTS,
  })

  const suggestions = useMemo(() => {
    const raw = subject ? data?.suggestions : []

    if (!raw?.length) {
      return raw ?? []
    }

    // Use 'in' narrowing — only Bill and mapped external invoices carry accountingBasis;
    // regular invoices leave it undefined, which the utility treats as non-cash (fail open).
    const accountingBasis = subject && 'accountingBasis' in subject ? subject.accountingBasis : undefined
    return raw.filter((suggestion) => !isCashBasedNonExactSuggestion(suggestion, accountingBasis))
  }, [data?.suggestions, subject])

  const subjectHasMatchSuggestion = useMemo(() => !!suggestions?.length, [suggestions?.length])

  return { suggestions, isLoading, subjectHasMatchSuggestion }
}
